import { useState, useEffect, type ReactNode } from 'react';
import {
	isRouteErrorResponse,
	Links,
	Meta,
	Outlet,
	Scripts,
	ScrollRestoration,
} from 'react-router';

import './app.css';

import type { Route } from './+types/root';

import Header from './components/header/header';

export const meta: Route.MetaFunction = () => [
	{ name: 'apple-mobile-web-app-capable', content: 'yes' },
	{ name: 'apple-mobile-web-app-title', content: 'Glass2Melt' },
	{ name: 'apple-mobile-web-app-status-bar-style', content: 'default' },

	{ name: 'msapplication-config', content: '/browserconfig.xml' },
	{ name: 'msapplication-TileColor', content: '#f0efea' },

	{ name: 'theme-color', content: '#f0efea' },
];

export const links: Route.LinksFunction = () => [
	{
		rel: 'preload',
		type: 'font/woff2',
		as: 'font',
		crossOrigin: 'anonymous',
		href: '/Inter-Regular.woff2',
	},
	{
		rel: 'preload',
		type: 'font/woff2',
		as: 'font',
		crossOrigin: 'anonymous',
		href: '/InterTight-SemiBold.woff2',
	},
	{
		rel: 'preload',
		as: 'image',
		fetchPriority: 'high',
		href: '/logo.svg',
	},
	{
		rel: 'icon',
		type: 'image/png',
		sizes: '96x96',
		href: '/favicon-96x96.png',
	},
	{
		rel: 'icon',
		type: 'image/svg+xml',
		sizes: '512x512',
		href: '/favicon-512x512.svg',
	},
	{ rel: 'shortcut icon', href: '/favicon.ico' },

	{
		rel: 'apple-touch-icon',
		sizes: '180x180',
		href: '/apple-touch-icon-180x180.png',
	},

	{ rel: 'manifest', href: '/site.webmanifest' },

	{ rel: 'mask-icon', href: '/favicon-16x16.svg', color: '#f0efea' },
];

export function Layout({ children }: { children: React.ReactNode }) {
	const [ready, setReady] = useState(false);

	useEffect(() => {
		let mounted = true;

		Promise.all([
			import('./services/init-services.client').then(
				async ({ initServices }) => {
					await initServices();
				},
			),
			new Promise<void>((resolve) => {
				if (document.readyState === 'complete') {
					return void setTimeout(() => resolve(), 0);
				}

				const onLoad = () => {
					window.removeEventListener('load', onLoad);

					resolve();
				};

				window.addEventListener('load', onLoad);
			}),
		])
			.then(() => {
				if (mounted) {
					setReady(true);
				}
			})
			.catch((error: unknown) => {
				if (mounted) {
					throw error;
				}
			});

		return () => {
			mounted = false;
		};
	}, []);

	let bodyContent: ReactNode = null;

	if (!ready) {
		bodyContent = (
			<div
				className="global-loader"
				role="status"
				aria-live="polite"
				aria-busy="true"
			>
				<style>{`
					@keyframes rr-uniform-pop {
						0%   { transform: scale(1); }
						25%  { transform: scale(0.90); }
						50%  { transform: scale(1); }
						75%  { transform: scale(0.94); }
						100% { transform: scale(1); }
					}

					.global-loader {
						position: fixed;
						inset: 0;
						display: flex;
						align-items: center;
						justify-content: center;
						background: #f0efea;
						z-index: 9999;
					}

					.global-loader__logo {
						width: 86px;
						height: auto;
						object-fit: contain;
						user-select: none;
						pointer-events: none;
						animation: rr-uniform-pop 1.9s cubic-bezier(0.25, 0.1, 0.25, 1) infinite;
						will-change: transform;
						transform-origin: center;
					}
				`}</style>

				<img
					src="/logo.svg"
					alt=""
					className="global-loader__logo"
					aria-hidden="true"
				/>
			</div>
		);
	} else {
		bodyContent = (
			<>
				<Header />
				{children}
			</>
		);
	}

	return (
		<html className="light" lang="en">
			<head>
				<meta charSet="utf-8" />
				<meta
					name="viewport"
					content="width=device-width, initial-scale=1"
				/>
				<Meta />
				<Links />
			</head>
			<body>
				{bodyContent}
				<ScrollRestoration />
				<Scripts />
			</body>
		</html>
	);
}

export default function App() {
	return <Outlet />;
}

export function ErrorBoundary({ error }: Route.ErrorBoundaryProps) {
	let message = 'Oops!';
	let details = 'An unexpected error occurred.';
	let stack: string | undefined;

	if (isRouteErrorResponse(error)) {
		message = error.status === 404 ? '404' : 'Error';
		details =
			error.status === 404
				? 'The requested page could not be found.'
				: error.statusText || details;
	} else if (import.meta.env.DEV && error && error instanceof Error) {
		details = error.message;
		stack = error.stack;
	}

	return (
		<>
			<h1>{message}</h1>
			<p>{details}</p>
			{stack && (
				<pre>
					<code>{stack}</code>
				</pre>
			)}
		</>
	);
}
